import React, { useContext /*, useState, useEffect, Fragment*/ } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import isWithinInterval from 'date-fns/isWithinInterval';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import endOfDay from 'date-fns/endOfDay';
import _get from 'lodash/get';
import _assign from 'lodash/assign';
import _map from 'lodash/map';
import InterviewBlockWeekly from '../../empDashboardMyCalendar/subComponents/InterviewBlockWeekly';
import MoreInterviewsBlockWeekly from '../../empDashboardMyCalendar/subComponents/MoreInterviewsBlockWeekly';
import { CALENDAR_HOUR_HEIGHT, CALENDAR_WEEKLY_HOURS_HEIGHT } from '../../common/constants';

const styles = theme => ({
    dayColumn: {
        flex: '1 1 auto',
        borderRight: '1px solid #F0F0F0',
        position: 'relative',
        '&:hover': {
            cursor: 'pointer',
        },
    },
    dayColumnAbsolute: {
        position: 'absolute',
        top: '0',
        bottom: '0',
        right: '0',
        left: '0',
    },
    dayColumnRelative: {
        flex: '1 1 auto',
        position: 'relative',
        paddingLeft: '2px',
        paddingRight: '2px',
        height: '100%',
    },
    dayColumnRelative2: {
        position: 'relative',
        height: '100%',
        width: '100%',
        flex: '1 1 auto',
    },
    hour: {
        borderBottom: '1px solid #F0F0F0',
        minHeight: `${CALENDAR_HOUR_HEIGHT}px`,
    },
    hourEnd: {
        borderBottom: '1px solid #F0F0F0',
        minHeight: `${CALENDAR_HOUR_HEIGHT}px`,
    },
});

// The start time of an interview is recalculated based on the current day,
//    so the start time of an interview can only be as early as the start time of the day.
const getInterviewStartDtForCurrentDay = (interviewStartDt, currentDay) => {
    if (isBefore(interviewStartDt, currentDay)) {
        return currentDay;
    } else {
        return interviewStartDt;
    }
};

// The end time of an interview is recalculated based on the current day,
//    so the end time of an interview can only be the end time of the day at the latest.
const getInterviewEndDtForCurrentDay = (interviewEndDt, currentDay) => {
    const endOfCurrentDay = endOfDay(currentDay);
    if (isAfter(interviewEndDt, endOfCurrentDay)) {
        return endOfCurrentDay;
    } else {
        return interviewEndDt;
    }
};

const getInterviewBlocks = params => {
    const { props /* setSelectedDate, defns, setInterviewCardAnchorEl, setClickedInterviewId, */ } = params;
    const { day, interviewList } = props;

    if (interviewList && interviewList.length > 0) {
        let interviewBlocks = [];
        let overLappingInterviews = [];
        overLappingInterviews.push(0);

        let currentInterval = {
            start: getInterviewStartDtForCurrentDay(interviewList[0].startDt.toDate(), day),
            end: getInterviewEndDtForCurrentDay(interviewList[0].endDt.toDate(), day),
        };

        interviewList.forEach((item, index, origArray) => {
            if (index !== 0) {
                let isStartWithin = isWithinInterval(getInterviewStartDtForCurrentDay(item.startDt.toDate(), day), currentInterval);
                let isEndWithin = isWithinInterval(getInterviewEndDtForCurrentDay(item.endDt.toDate(), day), currentInterval);

                if ((isStartWithin === true || isEndWithin === true) && index < origArray.length - 1) {
                    // Found an overlapping interview
                    currentInterval = _assign(currentInterval, { end: getInterviewEndDtForCurrentDay(item.endDt.toDate(), day) });
                    overLappingInterviews.push(index);
                } else {
                    // This interview does not overlap (or is the last interview), so push out any previous overlapping interviews

                    if ((isStartWithin === true || isEndWithin === true) && index === origArray.length - 1) {
                        // The last interview is an overlapping interview, so add it to the overlappingInterviews so it'll be pushed out.
                        currentInterval = _assign(currentInterval, { end: getInterviewEndDtForCurrentDay(item.endDt.toDate(), day) });
                        overLappingInterviews.push(index);
                    }

                    if (overLappingInterviews.length > 4) {
                        // There are more than 4 interviews overlapping, so show the MoreInterviewsBlock instead of the individual interviews.

                        const startDt = currentInterval.start;
                        const hourStartDt = format(startDt, 'H');
                        const minStartDt = format(startDt, 'm');

                        let topPixels = hourStartDt * (CALENDAR_HOUR_HEIGHT + 1);
                        topPixels += (minStartDt / 60) * (CALENDAR_HOUR_HEIGHT + 1);

                        const endDt = currentInterval.end;
                        const minInterview = differenceInMinutes(endDt, startDt);

                        let heightPixels = (minInterview / 60) * (CALENDAR_HOUR_HEIGHT + 1);

                        // TODO: Do I need this anymore since the start and end times of an interview are recalculated based on the current day,
                        //    so the start time can only be as early as the start time of the day and the end time can only be the end time of the
                        //    day at the latest.
                        if (topPixels + heightPixels > CALENDAR_WEEKLY_HOURS_HEIGHT) {
                            heightPixels = CALENDAR_WEEKLY_HOURS_HEIGHT - topPixels;
                        }

                        let moreInterviewList = _map(overLappingInterviews, (interviewIndex, idx) => {
                            return origArray[interviewIndex];
                        });

                        interviewBlocks.push(
                            <div style={{ position: 'absolute', top: `${topPixels}px`, height: `${heightPixels}px`, width: '100%' }}>
                                <MoreInterviewsBlockWeekly
                                    height={heightPixels}
                                    isHidden={false}
                                    remainingCount={overLappingInterviews.length}
                                    interviewList={moreInterviewList}
                                    dt={day}
                                />
                            </div>,
                        );
                    } else {
                        let blockPercentage;

                        if (overLappingInterviews.length === 1) {
                            blockPercentage = 100;
                        } else if (overLappingInterviews.length === 2) {
                            blockPercentage = 50;
                        } else if (overLappingInterviews.length === 3) {
                            blockPercentage = 33.33;
                        } else if (overLappingInterviews.length === 4) {
                            blockPercentage = 25;
                        }

                        overLappingInterviews.forEach((overlapItem, overlapIndex, overlapArray) => {
                            let leftPercentage = overlapIndex * blockPercentage;
                            let interview = origArray[overlapItem];

                            const startDt = getInterviewStartDtForCurrentDay(interview.startDt.toDate(), day);
                            const hourStartDt = format(startDt, 'H');
                            const minStartDt = format(startDt, 'm');

                            let topPixels = hourStartDt * (CALENDAR_HOUR_HEIGHT + 1);
                            topPixels += (minStartDt / 60) * (CALENDAR_HOUR_HEIGHT + 1);

                            const endDt = getInterviewEndDtForCurrentDay(interview.endDt.toDate(), day);

                            const minInterview = differenceInMinutes(endDt, startDt);

                            let heightPixels = (minInterview / 60) * (CALENDAR_HOUR_HEIGHT + 1);

                            // TODO: Do I need this anymore since the start and end times of an interview are recalculated based on the current day,
                            //    so the start time can only be as early as the start time of the day and the end time can only be the end time of the
                            //    day at the latest.
                            if (topPixels + heightPixels > CALENDAR_WEEKLY_HOURS_HEIGHT) {
                                heightPixels = CALENDAR_WEEKLY_HOURS_HEIGHT - topPixels;
                            }

                            interviewBlocks.push(
                                <div
                                    key={`interviewBlock-${index}-${overlapIndex}`}
                                    style={{ position: 'absolute', top: `${topPixels}px`, height: `${heightPixels}px`, left: `${leftPercentage}%`, width: `${blockPercentage}%` }}
                                >
                                    <InterviewBlockWeekly isHidden={false} interviewData={interview} dt={day} height={heightPixels} blockPercentage={blockPercentage} />
                                </div>,
                            );
                        });
                    }

                    if ((isStartWithin === true || isEndWithin === true) && index === origArray.length - 1) {
                        // The last interview is an overlapping interview, since they have all been pushed out, reset the overLappingInterviews array.
                        overLappingInterviews = [];
                    } else {
                        // All the overlapping interviews have been pushed out, so reset the currentInterval to the current non-overlapping interval
                        // and reset the overLappingInterviews to only include the current non-overlapping interval.
                        currentInterval = {
                            start: getInterviewStartDtForCurrentDay(item.startDt.toDate(), day),
                            end: getInterviewEndDtForCurrentDay(item.endDt.toDate(), day),
                        };
                        overLappingInterviews = [];
                        overLappingInterviews.push(index);
                    }
                }
            }
        });

        // There is one last non-overlapping interview left for the day, so print it out
        if (overLappingInterviews.length > 0) {
            let interview = interviewList[overLappingInterviews[0]];

            const startDt = getInterviewStartDtForCurrentDay(interview.startDt.toDate(), day);

            const hourStartDt = format(startDt, 'H');
            const minStartDt = format(startDt, 'm');

            let topPixels = hourStartDt * (CALENDAR_HOUR_HEIGHT + 1);
            topPixels += (minStartDt / 60) * (CALENDAR_HOUR_HEIGHT + 1);

            const endDt = getInterviewEndDtForCurrentDay(interview.endDt.toDate(), day);

            const minInterview = differenceInMinutes(endDt, startDt);

            let heightPixels = (minInterview / 60) * (CALENDAR_HOUR_HEIGHT + 1);

            // TODO: Do I need this anymore since the start and end times of an interview are recalculated based on the current day,
            //    so the start time can only be as early as the start time of the day and the end time can only be the end time of the
            //    day at the latest.
            if (topPixels + heightPixels > CALENDAR_WEEKLY_HOURS_HEIGHT) {
                heightPixels = CALENDAR_WEEKLY_HOURS_HEIGHT - topPixels;
            }

            interviewBlocks.push(
                <div key={`interviewBlock-${overLappingInterviews[0]}`} style={{ position: 'absolute', top: `${topPixels}px`, height: `${heightPixels}px`, width: '100%' }}>
                    <InterviewBlockWeekly isHidden={false} interviewData={interview} dt={day} height={heightPixels} />
                </div>,
            );
        }

        return interviewBlocks;
    } else {
        return null;
    }
};

const handleClickRoot = (setSelectedDate, evt) => {
    evt.preventDefault();
    setSelectedDate();
};

const getDefns = params => {
    const { props, setSelectedDate } = params;
    const { classes, day } = props;

    const defns = {
        dayColumn: {
            onClick: handleClickRoot.bind(null, setSelectedDate.bind(null, day)),
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

const HoursColumn = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    const context = useContext(LayoutContext);
    const setInterviewCardAnchorEl = _get(context, 'setInterviewCardAnchorEl');
    const setClickedInterviewId = _get(context, 'setClickedInterviewId');
    // const selectedDate = _get(context, 'selectedDate');
    const setSelectedDate = _get(context, 'setSelectedDate');
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    // const defns = getDefns({ props, setInterviewCardAnchorEl, setClickedInterviewId, selectedDate, setSelectedDate, });
    const defns = getDefns({ props, setSelectedDate });
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Render
    return (
        <div {...defns.dayColumn}>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hour}></div>
            <div {...defns.hourEnd}></div>
            <div {...defns.dayColumnAbsolute}>
                <div {...defns.dayColumnRelative}>
                    <div {...defns.dayColumnRelative2}>{getInterviewBlocks({ props, defns, setInterviewCardAnchorEl, setClickedInterviewId, setSelectedDate })}</div>
                </div>
            </div>
        </div>
    );
    //---------------------------------------------------------------------
};

export default withStyles(styles)(HoursColumn);
